import { createContext, useContext, useState, useEffect } from 'react'

const AuthContext = createContext()

const IS_DEMO = import.meta.env.DEV || window.location.hostname === 'localhost'

function getLocalUsers() {
  try {
    return JSON.parse(localStorage.getItem('tredex_users') || '[]')
  } catch {
    return []
  }
}

function saveLocalUsers(users) {
  localStorage.setItem('tredex_users', JSON.stringify(users))
}

function hashString(str) {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash
  }
  return btoa(hash.toString())
}

function createLocalToken(user) {
  const payload = btoa(JSON.stringify({ id: user.id, email: user.email, role: user.role, exp: Date.now() + 7 * 24 * 60 * 60 * 1000 }))
  const sig = btoa(hashString(payload + 'tredex-secret'))
  return payload + '.' + sig
}

function verifyLocalToken(token) {
  try {
    const [payload, sig] = token.split('.')
    if (sig !== btoa(hashString(payload + 'tredex-secret'))) return null
    const data = JSON.parse(atob(payload))
    if (data.exp < Date.now()) return null
    return data
  } catch {
    return null
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('tredex_token')
    if (token) {
      if (IS_DEMO) {
        const decoded = verifyLocalToken(token)
        if (decoded) {
          const users = getLocalUsers()
          const found = users.find(u => u.id === decoded.id)
          if (found) setUser(found)
          else localStorage.removeItem('tredex_token')
        } else {
          localStorage.removeItem('tredex_token')
        }
      } else {
        fetch('/api/auth/me', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
          .then(r => r.json())
          .then(data => {
            if (data.user) setUser(data.user)
            else localStorage.removeItem('tredex_token')
          })
          .catch(() => localStorage.removeItem('tredex_token'))
          .finally(() => setLoading(false))
      }
    } else {
      setLoading(false)
    }
  }, [])

  const login = async (email, password) => {
    if (IS_DEMO) {
      const users = getLocalUsers()
      const user = users.find(u => u.email === email)
      if (!user || user.password !== hashString(password + email)) {
        throw new Error('Invalid email or password')
      }
      const token = createLocalToken(user)
      localStorage.setItem('tredex_token', token)
      setUser(user)
      return { user, token }
    }

    const res = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    localStorage.setItem('tredex_token', data.token)
    setUser(data.user)
    return data
  }

  const signup = async (email, password, name) => {
    if (IS_DEMO) {
      const users = getLocalUsers()
      if (users.find(u => u.email === email)) {
        throw new Error('User already exists')
      }

      const isAdmin = email === 'dhanushkumar558@gmail.com'
      const newUser = {
        id: crypto.randomUUID(),
        email,
        password: hashString(password + email),
        name: name || email.split('@')[0],
        avatar: '',
        company: '',
        phone: '',
        createdAt: new Date().toISOString(),
        subscription: {
          plan: isAdmin ? 'yearly' : 'trial',
          startDate: new Date().toISOString(),
          endDate: isAdmin ? new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString() : new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
          status: 'active'
        },
        aiCallsUsed: 0,
        aiCallsLimit: isAdmin ? 999999 : 100,
        role: isAdmin ? 'admin' : 'user',
        emailVerified: isAdmin
      }

      users.push(newUser)
      saveLocalUsers(users)

      const token = createLocalToken(newUser)
      localStorage.setItem('tredex_token', token)
      setUser(newUser)
      return { user: newUser, token }
    }

    const res = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    localStorage.setItem('tredex_token', data.token)
    setUser(data.user)
    return data
  }

  const googleLogin = async (email, name, googleId) => {
    if (IS_DEMO) {
      const users = getLocalUsers()
      let user = users.find(u => u.email === email)
      if (!user) {
        user = {
          id: crypto.randomUUID(), email, password: '', name: name || email.split('@')[0],
          avatar: '', company: '', phone: '', googleId, createdAt: new Date().toISOString(),
          subscription: { plan: 'trial', startDate: new Date().toISOString(), endDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), status: 'active' },
          aiCallsUsed: 0, aiCallsLimit: 100, role: 'user', emailVerified: true
        }
        users.push(user)
        saveLocalUsers(users)
      }
      const token = createLocalToken(user)
      localStorage.setItem('tredex_token', token)
      setUser(user)
      return { user, token }
    }

    const res = await fetch('/api/auth/google', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, googleId })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    localStorage.setItem('tredex_token', data.token)
    setUser(data.user)
    return data
  }

  const forgotPassword = async (email) => {
    if (IS_DEMO) {
      return { message: 'If the email exists, a reset link has been sent.' }
    }

    const res = await fetch('/api/auth/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    return data
  }

  const resetPassword = async (token, newPassword) => {
    if (IS_DEMO) {
      return { message: 'Password reset successful. Please login.' }
    }

    const res = await fetch('/api/auth/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    return data
  }

  const updateProfile = async (profileData) => {
    if (IS_DEMO) {
      const users = getLocalUsers()
      const userIndex = users.findIndex(u => u.id === user.id)
      if (userIndex === -1) throw new Error('User not found')
      
      Object.assign(users[userIndex], profileData)
      saveLocalUsers(users)
      setUser(users[userIndex])
      return { user: users[userIndex] }
    }

    const token = localStorage.getItem('tredex_token')
    const res = await fetch('/api/auth/update-profile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(profileData)
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    setUser(data.user)
    return data
  }

  const changePassword = async (currentPassword, newPassword) => {
    if (IS_DEMO) {
      const users = getLocalUsers()
      const userIndex = users.findIndex(u => u.id === user.id)
      if (userIndex === -1) throw new Error('User not found')
      
      if (users[userIndex].password !== hashString(currentPassword + users[userIndex].email)) {
        throw new Error('Current password is incorrect')
      }
      
      users[userIndex].password = hashString(newPassword + users[userIndex].email)
      saveLocalUsers(users)
      return { message: 'Password changed successfully' }
    }

    const token = localStorage.getItem('tredex_token')
    const res = await fetch('/api/auth/change-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ currentPassword, newPassword })
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error)
    return data
  }

  const logout = () => {
    localStorage.removeItem('tredex_token')
    setUser(null)
  }

  const updateUser = (updatedUser) => {
    setUser(updatedUser)
  }

  return (
    <AuthContext.Provider value={{
      user, loading, login, signup, googleLogin,
      forgotPassword, resetPassword, updateProfile, changePassword,
      logout, updateUser
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)